import type { Improvement, Incentive } from '@prisma/client';

/**
 * Dollar value we credit for each reconditioning item a dealer includes.
 * Rough retail-replacement figures; tune as real deals come in.
 */
export const IMPROVEMENT_VALUES: Record<Improvement, number> = {
  CERTIFIED_PREOWNED: 1500,
  NEW_TIRES: 800,
  NEW_BRAKES: 500,
  NEW_ROTORS: 400,
  TRANSMISSION_SERVICE: 300,
  ALIGNMENT: 100,
  BATTERY: 250,
  AC_SERVICE: 250,
};

export const INCENTIVE_VALUES: Record<Incentive, number> = {
  FREE_OIL_CHANGES: 300,
  TIRE_ROTATIONS: 200,
  EXTENDED_WARRANTY: 2000,
  MAINTENANCE_PACKAGE: 1000,
};

export const IMPROVEMENT_LABELS: Record<Improvement, string> = {
  CERTIFIED_PREOWNED: 'Certified pre-owned',
  NEW_TIRES: 'New tires',
  NEW_BRAKES: 'New brakes',
  NEW_ROTORS: 'New rotors',
  TRANSMISSION_SERVICE: 'Transmission service',
  ALIGNMENT: 'Alignment',
  BATTERY: 'New battery',
  AC_SERVICE: 'A/C service',
};

export const INCENTIVE_LABELS: Record<Incentive, string> = {
  FREE_OIL_CHANGES: 'Free oil changes',
  TIRE_ROTATIONS: 'Tire rotations',
  EXTENDED_WARRANTY: 'Extended warranty',
  MAINTENANCE_PACKAGE: 'Maintenance package',
};

/** Each extra mile on the odometer costs the buyer about this much in resale value. */
export const COST_PER_MILE = 0.15;

/** How steeply the 0–100 score falls off: 1% higher effective cost than the best offer = -5 points. */
const SCORE_SLOPE = 500;

export interface ScorableOffer {
  id: string;
  price: number;
  mileage: number;
  listPrice?: number | null;
  improvements?: Improvement[] | null;
  incentives?: Incentive[] | null;
}

export interface OfferScore {
  id: string;
  /** Price + mileage penalty − extras. Lower is better. */
  effectiveCost: number;
  /** Dollar value of included improvements and incentives. */
  addedValue: number;
  /** 100 = best offer on the table; 0 = ≥20% more effective cost than the best. */
  score: number;
  /** 1-based, ties share a rank. */
  rank: number;
  /** listPrice − price, when the listing price is known. */
  savingsVsList?: number;
}

export const addedValue = (
  offer: Pick<ScorableOffer, 'improvements' | 'incentives'>
): number =>
  (offer.improvements ?? []).reduce(
    (sum, i) => sum + (IMPROVEMENT_VALUES[i] ?? 0),
    0
  ) +
  (offer.incentives ?? []).reduce(
    (sum, i) => sum + (INCENTIVE_VALUES[i] ?? 0),
    0
  );

export const effectiveCost = (offer: ScorableOffer): number =>
  offer.price + offer.mileage * COST_PER_MILE - addedValue(offer);

/**
 * Scores a set of priced offers against each other. Offers with no price are
 * ignored — pass only the ones the dealer has actually responded to.
 */
export const scoreOffers = (
  offers: ScorableOffer[]
): Map<string, OfferScore> => {
  const result = new Map<string, OfferScore>();
  const priced = offers.filter((o) => Number.isFinite(o.price) && o.price > 0);
  if (priced.length === 0) return result;

  const costs = priced.map((o) => ({ offer: o, cost: effectiveCost(o) }));
  const best = Math.min(...costs.map((c) => c.cost));
  const sorted = [...costs].sort((a, b) => a.cost - b.cost);

  let rank = 0;
  let previousCost: number | undefined;
  sorted.forEach((entry, index) => {
    if (previousCost === undefined || entry.cost !== previousCost) {
      rank = index + 1;
      previousCost = entry.cost;
    }
    const relative = best > 0 ? (entry.cost - best) / best : 0;
    const savings =
      entry.offer.listPrice && entry.offer.listPrice > 0
        ? entry.offer.listPrice - entry.offer.price
        : undefined;
    result.set(entry.offer.id, {
      id: entry.offer.id,
      effectiveCost: Math.round(entry.cost),
      addedValue: addedValue(entry.offer),
      score: Math.max(
        0,
        Math.min(100, Math.round(100 - relative * SCORE_SLOPE))
      ),
      rank,
      savingsVsList: savings,
    });
  });
  return result;
};

/** Green at 100, red at 0 — used for the score chip and card accent. */
export const scoreColor = (score: number, good: string, bad: string): string =>
  `color-mix(in srgb, ${bad} ${100 - score}%, ${good} ${score}%)`;

export const formatUsd = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? '—'
    : `$${Math.round(value).toLocaleString()}`;

/**
 * Fleet/rental risk flag — docs/manual-deal-workflow-findings.md §2. Rental-fleet trucks in
 * the segments checked ran ~30–37k mi/yr; personal-use trucks ran ~12–19k mi/yr, and mileage
 * turned out to be a much better proxy for personal-vs-fleet history than price alone.
 */
export const FLEET_RISK_MILES_PER_YEAR = 20000;

/**
 * Mileage per year, adjusted for vehicle age. Model years start appearing Oct 1 of the prior
 * calendar year, so a `year` vehicle's "birth" is treated as Oct 1 of (year − 1). Elapsed time
 * is floored at 0.5 years so a brand-new/current-model-year vehicle doesn't produce an
 * enormous (or divide-by-near-zero) annualized figure.
 */
export const annualizedMiles = (
  mileage: number,
  year: number,
  now: Date = new Date()
): number => {
  const modelYearStart = new Date(year - 1, 9, 1); // Oct 1 (month index 9)
  const msPerYear = 365.25 * 24 * 60 * 60 * 1000;
  const elapsedYears = (now.getTime() - modelYearStart.getTime()) / msPerYear;
  return mileage / Math.max(0.5, elapsedYears);
};

/** 'high' when age-adjusted annual mileage exceeds FLEET_RISK_MILES_PER_YEAR, else 'none'. */
export const fleetRisk = (
  mileage: number,
  year: number,
  now: Date = new Date()
): 'high' | 'none' =>
  annualizedMiles(mileage, year, now) > FLEET_RISK_MILES_PER_YEAR
    ? 'high'
    : 'none';
